/*
 * The frontmatter change table, rendered ABOVE the body diff.
 *
 * Frontmatter churn is most of the lines in a page commit and almost none of its
 * meaning: a bumped `updated`, a recomputed source hash, a `sources` list gaining
 * one entry. Left inline, that YAML buries the two lines of prose the edit was
 * actually about. Lifted into a table, both halves become readable — the fields as
 * a before/after, the body as a diff.
 *
 * Some of these fields are not the agent's to write. The platform maintains a
 * marked region of the block holding each source's path with the sha256 it had at
 * write time plus the conventions hash in force, which is what makes "stale" a
 * check rather than a judgement. Those rows are marked, because "the agent changed
 * this" and "the platform recorded this" are different claims about the same table.
 */
import type { ReactNode } from 'react'
import type { FrontmatterChange } from '../../../api/knowledgeBases.ts'

/**
 * Fields inside the platform-managed provenance region.
 *
 * Carried here because the diff payload has no marker for them (recorded in
 * `API-GAPS.md`); when the contract grows a flag, read it instead of this set.
 */
const PLATFORM_MANAGED = new Set(['sources', 'schema_sha256'])

interface FrontmatterTableProps {
  changes: FrontmatterChange[]
}

export function FrontmatterTable({ changes }: FrontmatterTableProps) {
  if (changes.length === 0) {
    return <p className="field__hint">Frontmatter не менялся.</p>
  }
  return (
    <>
      <table className="fm">
        <thead>
          <tr>
            <th>Поле</th>
            <th>Было</th>
            <th>Стало</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change) => {
            const managed = PLATFORM_MANAGED.has(change.field)
            const changed = change.before !== change.after
            return (
              <tr key={change.field} className={managed ? 'protected' : undefined}>
                <td className="k">
                  {change.field}
                  {managed ? (
                    <span title="Ведётся платформой: путь источника и его sha256 на момент записи">
                      {' '}
                      🔒
                    </span>
                  ) : null}
                </td>
                <td>{render(change.before)}</td>
                <td className={changed && !managed ? 'changed' : undefined}>
                  {render(change.after)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="field__hint" style={{ marginTop: 9 }}>
        Frontmatter вынесен в таблицу отдельно — иначе YAML-шум забивает собой дифф тела, а смысл
        правки именно в нём. 🔒 — поля, которые пишет платформа, а не агент.
      </p>
    </>
  )
}

/** Absent (`null`/omitted) means no value on that side — not the same as empty. */
function render(value: string | null | undefined): ReactNode {
  if (value === null || value === undefined) return <span className="muted-2">—</span>
  if (value === '') return <span className="muted-2">(пусто)</span>
  return value
}
